import { calendarDateKey, calendarDateToLocal, localDateKey } from '../../../utils/calendarDate';
import React, { useMemo } from 'react';
import { useTesterDueThisWeek } from '../../../hooks/analytics/useTesterReports.ts';
import { WidgetCard } from '../controller/shared.tsx';
import type { TesterDueThisWeekRow } from '../../../request/analytics/testerReports.ts';

function toDateKey(iso: string): string {
  return calendarDateKey(iso);
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Report #31 — My Due This Week. 7-day calendar strip: one tile per day
 * (today .. today+6) showing how many of the caller's controls are due that
 * day, plus a separate overdue bucket (due_date in the past). Overdue and
 * "due today" tiles render red.
 */
export const DueThisWeekStrip: React.FC<{ controlIdFilter?: Set<string> | null; assigneeUserId?: number | null }> = ({
  controlIdFilter,
  assigneeUserId,
}) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useTesterDueThisWeek(assigneeUserId);
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);

  const { days, overdueCount, overdueControls } = useMemo(() => {
    const today = startOfToday();
    const byDay = new Map<string, TesterDueThisWeekRow[]>();
    const days: { key: string; date: Date; rows: TesterDueThisWeekRow[] }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const key = localDateKey(d);
      byDay.set(key, []);
      days.push({ key, date: d, rows: [] });
    }

    const overdueControls: TesterDueThisWeekRow[] = [];
    rows.forEach((row) => {
      if (!row.due_date) return;
      const due = calendarDateToLocal(row.due_date);
      if (!due) return;
      due.setHours(0, 0, 0, 0);
      if (due.getTime() < today.getTime()) {
        overdueControls.push(row);
        return;
      }
      const key = toDateKey(row.due_date);
      const bucket = byDay.get(key);
      if (bucket) bucket.push(row);
    });

    days.forEach((d) => {
      d.rows = byDay.get(d.key) ?? [];
    });

    return { days, overdueCount: overdueControls.length, overdueControls };
  }, [rows]);

  return (
    <WidgetCard
      title="Due This Week"
      description="Your assigned controls due in the next 7 days"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load due-this-week data.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="Nothing due this week."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="analytics-scroll flex gap-2 overflow-x-auto pb-1">
        {overdueCount > 0 && (
          <div className="shrink-0 w-24 rounded-lg border border-red-200 bg-red-50 p-3 text-center">
            <p className="text-[11px] font-semibold text-red-600 uppercase tracking-wide">Overdue</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{overdueCount}</p>
            <p className="text-[10px] text-red-500 mt-0.5 truncate" title={overdueControls.map((r) => r.control_id).join(', ')}>
              control{overdueCount === 1 ? '' : 's'}
            </p>
          </div>
        )}
        {days.map(({ key, date, rows: dayRows }, idx) => {
          const isToday = idx === 0;
          const hasItems = dayRows.length > 0;
          return (
            <div
              key={key}
              className={`shrink-0 w-24 rounded-lg border p-3 text-center ${
                isToday && hasItems
                  ? 'border-amber-300 bg-amber-50'
                  : hasItems
                  ? 'border-blue-200 bg-blue-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                {isToday ? 'Today' : date.toLocaleDateString(undefined, { weekday: 'short' })}
              </p>
              <p className="text-[10px] text-gray-400">{date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>
              <p className={`text-2xl font-bold mt-1 ${hasItems ? (isToday ? 'text-amber-700' : 'text-blue-700') : 'text-gray-300'}`}>
                {dayRows.length}
              </p>
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
};

export default DueThisWeekStrip;
